import { getApiUrl } from '@/lib/api-config';

/**
 * The ONE "delete an attachment" call: a single DELETE to
 * `/api/entities/attachment/:id`.
 *
 * The backend owns the whole operation (AttachmentsService.delete): it
 * soft-deletes the row (`deleted_at` -- every list/count query filters on it,
 * see backend core/entity-counts.js) AND removes the real Storage object from the
 * `attachments` bucket, with the caller's own session so Storage RLS still
 * applies. The browser must not also touch the table or Storage (two writers
 * for one object: if the API call then failed, the file was already gone
 * while its row stayed).
 *
 * Callers must not call this twice for one deletion: the first DELETE
 * soft-deletes the row, so a second one 404s (see each page's
 * handleDeleteAttachment -- it only updates that page's local cache).
 */
export async function deleteEntityAttachment(opts: {
  attachmentId: string;
  userId: string | undefined;
}): Promise<boolean> {
  const { attachmentId, userId } = opts;
  const logPrefix = '[attachment-delete]';

  if (!attachmentId) {
    console.error(`${logPrefix} missing attachmentId`);
    return false;
  }
  if (!userId) {
    console.error(`${logPrefix} missing userId`);
    return false;
  }

  try {
    const response = await fetch(getApiUrl(`api/entities/attachment/${attachmentId}`), {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      console.error(`${logPrefix} API delete failed`, response.status);
      return false;
    }
    return true;
  } catch (err) {
    console.error(`${logPrefix} unexpected error`, err);
    return false;
  }
}
